import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, MapPin, ChevronDown, ChevronUp, Loader2, ShoppingBag, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_STATUSES = {
  'paid - awaiting supplier': { step: 1, label: 'Processing', color: 'text-yellow-500', bgColor: 'bg-yellow-500' },
  'order sent to supplier': { step: 2, label: 'Sent to Supplier', color: 'text-blue-500', bgColor: 'bg-blue-500' },
  'dispatched': { step: 3, label: 'Dispatched', color: 'text-purple-500', bgColor: 'bg-purple-500' },
  'in transit': { step: 4, label: 'In Transit', color: 'text-orange-500', bgColor: 'bg-orange-500' },
  'delivered': { step: 5, label: 'Delivered', color: 'text-green-500', bgColor: 'bg-green-500' }
};

const MyOrders = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusInfo = (status) => {
    return ORDER_STATUSES[status] || { step: 0, label: status, color: 'text-gray-500', bgColor: 'bg-gray-500' };
  };

  const StatusTimeline = ({ currentStatus }) => {
    const statusInfo = getStatusInfo(currentStatus);
    const currentStep = statusInfo.step;

    return (
      <div className="flex items-center justify-between w-full">
        {Object.entries(ORDER_STATUSES).map(([key, value], index) => (
          <div key={key} className="flex items-center">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                value.step <= currentStep ? value.bgColor : 'bg-[#2d352d]'
              }`}>
                {value.step < currentStep ? (
                  <CheckCircle className="w-4 h-4 text-white" />
                ) : value.step === currentStep ? (
                  <Clock className="w-4 h-4 text-white" />
                ) : (
                  <span className="text-[#6b7d6b] text-xs">{value.step}</span>
                )}
              </div>
              <span className={`text-xs mt-1 hidden sm:block ${value.step <= currentStep ? value.color : 'text-[#6b7d6b]'}`}>
                {value.label}
              </span>
            </div>
            {index < Object.keys(ORDER_STATUSES).length - 1 && (
              <div className={`w-8 sm:w-16 h-1 mx-1 ${
                value.step < currentStep ? value.bgColor : 'bg-[#2d352d]'
              }`} />
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050605] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#c8b273] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050605] py-8 px-4 md:px-8" data-testid="my-orders-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase tracking-wider text-white mb-2">
          My Orders
        </h1>
        <p className="text-[#8a948a] mb-8">Track and manage your orders</p>

        {orders.length === 0 ? (
          <motion.div 
            className="glass-card p-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Package className="w-16 h-16 text-[#4a504a] mx-auto mb-4" />
            <h2 className="text-xl text-white mb-2">No Orders Yet</h2>
            <p className="text-[#8a948a] mb-6">You haven't placed any orders yet. Start shopping!</p>
            <Button onClick={() => navigate('/shop')} className="btn-primary">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Browse Shop
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {orders.map((order, index) => {
              const statusInfo = getStatusInfo(order.order_status);
              const isExpanded = expandedOrder === order.order_id;

              return (
                <motion.div
                  key={order.order_id}
                  className="glass-card overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {/* Order Header */}
                  <div 
                    className="p-4 sm:p-6 cursor-pointer"
                    onClick={() => setExpandedOrder(isExpanded ? null : order.order_id)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                      <div>
                        <p className="text-[#6b7d6b] text-sm">Order #{order.order_id}</p>
                        <p className="text-[#8a948a] text-sm">{formatDate(order.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor}/20 ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        <p className="text-white font-semibold">£{order.total_amount?.toFixed(2)}</p>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-[#6b7d6b]" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-[#6b7d6b]" />
                        )}
                      </div>
                    </div>

                    {/* Status Timeline */}
                    <StatusTimeline currentStatus={order.order_status} />
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-white/10"
                    >
                      <div className="p-4 sm:p-6 space-y-6">
                        {/* Tracking Number */}
                        {order.tracking_number && (
                          <div className="bg-[#0b0d0b] rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Truck className="w-5 h-5 text-[#c8b273]" />
                              <span className="text-white font-medium">Tracking Number</span>
                            </div>
                            <p className="text-[#c8b273] font-mono text-lg">{order.tracking_number}</p>
                          </div>
                        )}

                        {/* Items */}
                        <div>
                          <h4 className="text-white font-medium mb-3">Items ({order.items?.length})</h4>
                          <div className="space-y-2">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                                <div>
                                  <p className="text-white">{item.product_name}</p>
                                  <p className="text-[#6b7d6b] text-sm">
                                    SKU: {item.sku} | Qty: {item.quantity}
                                  </p>
                                </div>
                                <p className="text-[#c8b273]">£{(item.unit_price * item.quantity).toFixed(2)}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Delivery Address */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <MapPin className="w-4 h-4 text-[#c8b273]" />
                            <span className="text-white font-medium">Delivery Address</span>
                          </div>
                          <p className="text-[#8a948a] whitespace-pre-line">{order.customer?.shipping_address}</p>
                        </div>

                        {/* Payment Info */}
                        {order.stripe_payment_id && (
                          <div className="text-[#6b7d6b] text-sm">
                            Payment Reference: {order.stripe_payment_id}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrders;
